import { useDataExplorerAppearance } from './appearanceContext';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  defaultAnimateLayoutChanges,
  SortableContext,
  type AnimateLayoutChanges,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { resolveDataExplorerAppearance } from './theme';
import type {
  DataExplorerDraggableTreeClassNames,
  DataExplorerDraggableTreeDragPayload,
  DataExplorerDraggableTreeFlattenedNode,
  DataExplorerDraggableTreeNode,
  DataExplorerDraggableTreeProps,
  DataExplorerDraggableTreeRenderContext,
} from './types';
import { cn } from './utils';

type FlattenedNode<TSource, TContext> =
  DataExplorerDraggableTreeFlattenedNode<TSource, TContext>;
type TreeItemRadius = DataExplorerDraggableTreeProps['itemRadius'];
type ClientPoint = { x: number; y: number };
type DragPreviewLayout = {
  initialLeft: number;
  initialTop: number;
  left: number;
  top: number;
  width?: number;
  height?: number;
};
type ProjectedDrop = {
  parentId?: string;
  index: number;
  depth: number;
};

const treeItemDropTransition = {
  duration: 220,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

const treeItemAnimateLayoutChanges: AnimateLayoutChanges = (args) => {
  if (args.isSorting) return true;
  if (args.wasDragging && args.transition && args.containerId === args.previousContainerId) {
    return true;
  }
  return defaultAnimateLayoutChanges(args);
};

const treeItemRadiusValues = {
  none: '0px',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
};

function resolveTreeItemBorderRadius(
  itemRadius: TreeItemRadius,
  appearanceRadius: keyof typeof treeItemRadiusValues,
) {
  const radius = itemRadius ?? appearanceRadius;
  if (typeof radius === 'number') return `${radius}px`;
  if (!radius) return undefined;
  return treeItemRadiusValues[radius as keyof typeof treeItemRadiusValues] ?? radius;
}

function getEventClientPoint(event: Event | undefined): ClientPoint | undefined {
  if (!event) return undefined;
  if ('clientX' in event && 'clientY' in event) {
    return {
      x: Number((event as MouseEvent | PointerEvent).clientX),
      y: Number((event as MouseEvent | PointerEvent).clientY),
    };
  }

  const touchEvent = event as TouchEvent;
  const touch = touchEvent.touches?.[0] ?? touchEvent.changedTouches?.[0];
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={cn(
        'h-4 w-4 transition-transform duration-200 ease-out',
        expanded ? 'rotate-90' : 'rotate-0',
      )}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function GripIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="9" cy="5" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="19" r="1" />
      <circle cx="15" cy="5" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="19" r="1" />
    </svg>
  );
}

function collectDefaultExpandedNodeIds<TSource, TContext>(
  nodes: Array<DataExplorerDraggableTreeNode<TSource, TContext>>,
  ids: Set<string>,
) {
  for (const node of nodes) {
    if (node.defaultExpanded) ids.add(node.id);
    collectDefaultExpandedNodeIds(node.children ?? [], ids);
  }
}

function buildDefaultExpandedNodeIds<TSource, TContext>(
  nodes: Array<DataExplorerDraggableTreeNode<TSource, TContext>>,
  defaultExpandedNodeIds: string[] | undefined,
) {
  const ids = new Set(defaultExpandedNodeIds ?? []);
  collectDefaultExpandedNodeIds(nodes, ids);
  return ids;
}

function flattenTree<TSource, TContext>(
  nodes: Array<DataExplorerDraggableTreeNode<TSource, TContext>>,
  expandedNodeIds: Set<string>,
  parentId: string | undefined,
  depth: number,
  entries: FlattenedNode<TSource, TContext>[],
) {
  nodes.forEach((node, index) => {
    entries.push({ node, parentId, index, depth });
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isExpanded = node.collapsible === false || expandedNodeIds.has(node.id);
    if (hasChildren && isExpanded) {
      flattenTree(node.children ?? [], expandedNodeIds, node.id, depth + 1, entries);
    }
  });
}

function buildFlattenedTree<TSource, TContext>(
  nodes: Array<DataExplorerDraggableTreeNode<TSource, TContext>>,
  expandedNodeIds: Set<string>,
) {
  const entries: FlattenedNode<TSource, TContext>[] = [];
  flattenTree(nodes, expandedNodeIds, undefined, 0, entries);
  return entries;
}

function findAncestorEntryAtDepth<TSource, TContext>(
  entries: Array<FlattenedNode<TSource, TContext>>,
  entry: FlattenedNode<TSource, TContext>,
  depth: number,
) {
  if (depth < 0) return undefined;
  let parentId = entry.parentId;
  while (parentId) {
    const parentEntry = entries.find((candidate) => candidate.node.id === parentId);
    if (!parentEntry) return undefined;
    if (parentEntry.depth === depth) return parentEntry;
    parentId = parentEntry.parentId;
  }
  return undefined;
}

function countSiblingsBeforeDrop<TSource, TContext>(
  entries: Array<FlattenedNode<TSource, TContext>>,
  activeEntry: FlattenedNode<TSource, TContext>,
  overEntry: FlattenedNode<TSource, TContext>,
  parentId: string | undefined,
  targetDepth: number,
) {
  const targetEntry = activeEntry.node.id === overEntry.node.id ? activeEntry : overEntry;
  let index = 0;
  for (const entry of entries) {
    if (entry.node.id === targetEntry.node.id) break;
    if (entry.node.id === activeEntry.node.id) continue;
    if (entry.parentId === parentId && entry.depth === targetDepth) {
      index += 1;
    }
  }
  return index;
}

export function projectDataExplorerDraggableTreeDrop<TSource, TContext>({
  flattenedNodes,
  activeEntry,
  overEntry,
  dragOffsetX,
  indentSize,
}: {
  flattenedNodes: Array<FlattenedNode<TSource, TContext>>;
  activeEntry: FlattenedNode<TSource, TContext>;
  overEntry: FlattenedNode<TSource, TContext>;
  dragOffsetX: number;
  indentSize: number;
}): ProjectedDrop {
  const depthDelta = Math.round(dragOffsetX / Math.max(1, indentSize));
  const projectedDepth = Math.max(0, activeEntry.depth + depthDelta);
  const targetDepth = Math.min(projectedDepth, overEntry.depth);
  const parentAtDepth =
    targetDepth === 0
      ? undefined
      : targetDepth === overEntry.depth
        ? overEntry.parentId
        : findAncestorEntryAtDepth(flattenedNodes, overEntry, targetDepth)?.parentId;

  return {
    parentId: parentAtDepth,
    index: countSiblingsBeforeDrop(
      flattenedNodes,
      activeEntry,
      overEntry,
      parentAtDepth,
      targetDepth,
    ),
    depth: targetDepth,
  };
}

function DraggableTreeItem<TSource, TContext>({
  entry,
  selected,
  expanded,
  active,
  over,
  indentSize,
  activationMode,
  dragHandleLabel,
  expandLabel,
  collapseLabel,
  themeSlots,
  classNames,
  itemStyle,
  context,
  renderNodeActions,
  renderNodeChildren,
  onSelect,
  onToggle,
  registerNodeElement,
}: {
  entry: FlattenedNode<TSource, TContext>;
  selected: boolean;
  expanded: boolean;
  active: boolean;
  over: boolean;
  indentSize: number;
  activationMode: 'row' | 'handle';
  dragHandleLabel: string;
  expandLabel: string;
  collapseLabel: string;
  themeSlots: ReturnType<typeof resolveDataExplorerAppearance>['slots'];
  classNames?: DataExplorerDraggableTreeClassNames;
  itemStyle?: CSSProperties;
  context?: TContext;
  renderNodeActions?: DataExplorerDraggableTreeProps<
    TSource,
    TContext
  >['renderNodeActions'];
  renderNodeChildren?: DataExplorerDraggableTreeProps<
    TSource,
    TContext
  >['renderNodeChildren'];
  onSelect?: (node: DataExplorerDraggableTreeNode<TSource, TContext>) => void;
  onToggle: (nodeId: string) => void;
  registerNodeElement?: (nodeId: string, element: HTMLDivElement | null) => void;
}) {
  const { node, depth } = entry;
  const hasChildren = node.collapsible === true || Boolean(node.children?.length);
  const disabled = Boolean(node.disabled);
  const selectable = node.selectable !== false && !disabled;
  const draggable = node.draggable !== false && !disabled;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: node.id,
    disabled: !draggable,
    animateLayoutChanges: treeItemAnimateLayoutChanges,
    transition: treeItemDropTransition,
  });
  const {
    role: _sortableRole,
    tabIndex: _sortableTabIndex,
    'aria-disabled': _sortableAriaDisabled,
    ...sortableAttributes
  } = attributes;
  const sortableListeners = activationMode === 'row' ? listeners : undefined;
  const handleListeners = activationMode === 'handle' ? listeners : undefined;
  const renderContext: DataExplorerDraggableTreeRenderContext<TSource, TContext> = {
    node,
    depth,
    expanded,
    selected,
    dragging: isDragging || active,
    disabled,
    context: node.context ?? context,
  };
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const setRowRef = useCallback(
    (element: HTMLDivElement | null) => {
      setNodeRef(element);
      registerNodeElement?.(node.id, element);
    },
    [node.id, registerNodeElement, setNodeRef],
  );

  return (
    <div
      style={style}
      className={cn(
        'min-w-0',
        classNames?.item,
        node.className,
        isDragging && 'relative z-20',
        active && 'opacity-0',
      )}
    >
      <TreeItemRow
        node={node}
        depth={depth}
        indentSize={indentSize}
        selected={selected}
        expanded={expanded}
        active={isDragging || active}
        over={over}
        hasChildren={hasChildren}
        disabled={disabled}
        selectable={selectable}
        draggable={draggable}
        activationMode={activationMode}
        dragHandleLabel={dragHandleLabel}
        expandLabel={expandLabel}
        collapseLabel={collapseLabel}
        themeSlots={themeSlots}
        classNames={classNames}
        itemClassName={node.itemClassName}
        itemStyle={itemStyle}
        sortableAttributes={sortableAttributes}
        sortableListeners={sortableListeners}
        handleListeners={handleListeners}
        renderNodeActions={renderNodeActions}
        renderContext={renderContext}
        rowRef={setRowRef}
        dragHandleRef={activationMode === 'handle' ? setActivatorNodeRef : undefined}
        onSelect={() => onSelect?.(node)}
        onToggle={() => onToggle(node.id)}
      />
      {renderNodeChildren ? (
        <div
          className={cn(
            'min-w-0',
            classNames?.children,
            node.childrenClassName,
          )}
        >
          {renderNodeChildren(renderContext)}
        </div>
      ) : null}
    </div>
  );
}

function TreeItemRow<TSource, TContext>({
  node,
  depth,
  indentSize,
  selected,
  expanded,
  active,
  over,
  hasChildren,
  disabled,
  selectable,
  draggable,
  activationMode,
  dragHandleLabel,
  expandLabel,
  collapseLabel,
  themeSlots,
  classNames,
  itemClassName,
  itemStyle,
  sortableAttributes,
  sortableListeners,
  handleListeners,
  renderNodeActions,
  renderContext,
  rowRef,
  dragHandleRef,
  onSelect,
  onToggle,
}: {
  node: DataExplorerDraggableTreeNode<TSource, TContext>;
  depth: number;
  indentSize: number;
  selected: boolean;
  expanded: boolean;
  active: boolean;
  over: boolean;
  hasChildren: boolean;
  disabled: boolean;
  selectable: boolean;
  draggable: boolean;
  activationMode: 'row' | 'handle';
  dragHandleLabel: string;
  expandLabel: string;
  collapseLabel: string;
  themeSlots: ReturnType<typeof resolveDataExplorerAppearance>['slots'];
  classNames?: DataExplorerDraggableTreeClassNames;
  itemClassName?: string;
  itemStyle?: CSSProperties;
  sortableAttributes?: Record<string, unknown>;
  sortableListeners?: Record<string, unknown>;
  handleListeners?: Record<string, unknown>;
  renderNodeActions?: DataExplorerDraggableTreeProps<
    TSource,
    TContext
  >['renderNodeActions'];
  renderContext: DataExplorerDraggableTreeRenderContext<TSource, TContext>;
  rowRef?: (node: HTMLDivElement | null) => void;
  dragHandleRef?: (node: HTMLElement | null) => void;
  onSelect?: () => void;
  onToggle?: () => void;
}) {
  return (
    <div
      ref={rowRef}
      data-data-explorer-draggable-tree-item
      className={cn(
        themeSlots.treeNodeButton,
        'min-h-8 touch-none border border-transparent bg-background/70 py-0 pl-1 pr-2 font-medium',
        selectable && 'cursor-pointer',
        draggable && activationMode === 'row' && 'cursor-grab active:cursor-grabbing',
        selected && themeSlots.treeNodeSelected,
        disabled && themeSlots.treeNodeDisabled,
        active && themeSlots.treeNodeDragging,
        over && 'border-primary/30 bg-primary/[0.08] ring-1 ring-primary/15',
        selected && classNames?.itemSelected,
        disabled && classNames?.itemDisabled,
        active && classNames?.itemDragging,
        over && classNames?.itemOver,
        classNames?.itemButton,
        itemClassName,
      )}
      style={{ ...itemStyle, paddingLeft: `${depth * indentSize + 4}px` }}
      role="treeitem"
      aria-selected={selected || undefined}
      aria-disabled={disabled || undefined}
      aria-expanded={hasChildren ? expanded : undefined}
      aria-label={node.ariaLabel}
      tabIndex={selectable ? 0 : -1}
      {...sortableAttributes}
      {...sortableListeners}
      onClick={() => {
        if (!selectable) return;
        onSelect?.();
      }}
      onKeyDown={(event) => {
        if (!selectable) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSelect?.();
      }}
    >
      {draggable ? (
        <span
          ref={dragHandleRef}
          className={cn(
            themeSlots.treeNodeDragHandle,
            'opacity-100',
            classNames?.dragHandle,
          )}
          aria-label={dragHandleLabel}
          title={dragHandleLabel}
          {...handleListeners}
          onClick={(event) => event.stopPropagation()}
        >
          <GripIcon />
        </span>
      ) : (
        <span className={cn('h-5 w-5 shrink-0', classNames?.dragHandle)} />
      )}
      {hasChildren ? (
        <button
          type="button"
          className={cn(themeSlots.treeNodeToggle, classNames?.toggle)}
          aria-label={expanded ? collapseLabel : expandLabel}
          onClick={(event) => {
            event.stopPropagation();
            onToggle?.();
          }}
        >
          <ChevronIcon expanded={expanded} />
        </button>
      ) : (
        <span className={cn(themeSlots.treeNodeToggle, classNames?.toggle)} />
      )}
      {node.icon ? (
        <span className={cn(themeSlots.treeNodeIcon, classNames?.icon)}>
          {node.icon}
        </span>
      ) : null}
      <span
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          classNames?.itemContent,
        )}
      >
        <span className={cn(themeSlots.treeNodeLabel, classNames?.label)}>
          {node.label}
        </span>
        {node.description ? (
          <span
            className={cn(
              themeSlots.treeNodeDescription,
              classNames?.description,
            )}
          >
            {node.description}
          </span>
        ) : null}
      </span>
      {node.meta ? (
        <span className={cn(themeSlots.treeNodeMeta, classNames?.meta)}>
          {node.meta}
        </span>
      ) : null}
      {node.badge ? (
        <span className={cn(themeSlots.treeNodeBadge, classNames?.badge)}>
          {node.badge}
        </span>
      ) : null}
      {renderNodeActions ? (
        <span className={cn('ml-1 shrink-0', classNames?.actions)}>
          {renderNodeActions(renderContext)}
        </span>
      ) : null}
    </div>
  );
}

export function DataExplorerDraggableTree<
  TSource = unknown,
  TContext = unknown,
>({
  nodes,
  context,
  selectedNodeId,
  expandedNodeIds,
  defaultExpandedNodeIds,
  resetExpansionKey,
  indentSize = 16,
  activationMode = 'row',
  loading,
  empty,
  labels,
  appearance,
  itemRadius,
  className,
  classNames,
  renderNodeActions,
  renderNodeChildren,
  onSelectNode,
  onExpandedNodeIdsChange,
  onDragEnd,
}: DataExplorerDraggableTreeProps<TSource, TContext>) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const [internalExpandedNodeIds, setInternalExpandedNodeIds] = useState(
    () => buildDefaultExpandedNodeIds(nodes, defaultExpandedNodeIds),
  );
  const [activeNodeId, setActiveNodeId] = useState<string | undefined>();
  const [dragPreviewLayout, setDragPreviewLayout] = useState<
    DragPreviewLayout | undefined
  >();
  const [overNodeId, setOverNodeId] = useState<string | undefined>();
  const nodeElementsRef = useRef(new Map<string, HTMLDivElement>());
  const itemBorderRadius = resolveTreeItemBorderRadius(itemRadius, theme.radius);
  const itemStyle = itemBorderRadius
    ? ({ borderRadius: itemBorderRadius } satisfies CSSProperties)
    : undefined;
  const overlayStyle = {
    width: dragPreviewLayout?.width,
    height: dragPreviewLayout?.height,
    borderRadius: itemBorderRadius,
  } satisfies CSSProperties;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    setInternalExpandedNodeIds(
      buildDefaultExpandedNodeIds(nodes, defaultExpandedNodeIds),
    );
  }, [defaultExpandedNodeIds, resetExpansionKey]);

  const resolvedExpandedNodeIds =
    expandedNodeIds !== undefined
      ? new Set(expandedNodeIds)
      : internalExpandedNodeIds;
  const flattenedNodes = useMemo(
    () => buildFlattenedTree(nodes, resolvedExpandedNodeIds),
    [nodes, resolvedExpandedNodeIds],
  );
  const flattenedNodeMap = useMemo(() => {
    const map = new Map<string, FlattenedNode<TSource, TContext>>();
    for (const entry of flattenedNodes) map.set(entry.node.id, entry);
    return map;
  }, [flattenedNodes]);
  const activeEntry = activeNodeId ? flattenedNodeMap.get(activeNodeId) : undefined;
  const sortableIds = flattenedNodes.map((entry) => entry.node.id);
  const registerNodeElement = useCallback(
    (nodeId: string, element: HTMLDivElement | null) => {
      if (element) {
        nodeElementsRef.current.set(nodeId, element);
      } else {
        nodeElementsRef.current.delete(nodeId);
      }
    },
    [],
  );

  const clearDragState = useCallback(() => {
    setActiveNodeId(undefined);
    setDragPreviewLayout(undefined);
    setOverNodeId(undefined);
  }, []);

  const setNodeExpanded = (nodeId: string, expanded: boolean) => {
    const next = new Set(resolvedExpandedNodeIds);
    if (expanded) {
      next.add(nodeId);
    } else {
      next.delete(nodeId);
    }
    if (expandedNodeIds === undefined) {
      setInternalExpandedNodeIds(next);
    }
    onExpandedNodeIdsChange?.([...next]);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id || '');
    setActiveNodeId(activeId);

    const elementRect = activeId
      ? nodeElementsRef.current.get(activeId)?.getBoundingClientRect()
      : undefined;
    const initialRect = event.active.rect.current.initial;
    const rect = elementRect ?? initialRect;
    const point = getEventClientPoint(event.activatorEvent);
    const width = rect?.width;
    const height = rect?.height;
    const left = rect?.left ?? (point ? point.x - (width ?? 0) / 2 : 0);
    const top = rect?.top ?? (point ? point.y - (height ?? 0) / 2 : 0);

    setDragPreviewLayout({
      initialLeft: left,
      initialTop: top,
      left,
      top,
      width,
      height,
    });
  };

  const handleDragMove = (event: DragMoveEvent) => {
    setDragPreviewLayout((layout) =>
      layout
        ? {
            ...layout,
            left: layout.initialLeft + event.delta.x,
            top: layout.initialTop + event.delta.y,
          }
        : layout,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id || '');
    const overId = event.over ? String(event.over.id || '') : '';
    if (!activeId || !overId || !onDragEnd) {
      clearDragState();
      return;
    }
    const activeEntry = flattenedNodeMap.get(activeId);
    const overEntry = flattenedNodeMap.get(overId);
    if (!activeEntry || !overEntry) {
      clearDragState();
      return;
    }
    const projectedDrop = projectDataExplorerDraggableTreeDrop({
      flattenedNodes,
      activeEntry,
      overEntry,
      dragOffsetX: event.delta.x,
      indentSize,
    });
    if (
      activeEntry.parentId === projectedDrop.parentId &&
      activeEntry.index === projectedDrop.index
    ) {
      clearDragState();
      return;
    }
    const payload: DataExplorerDraggableTreeDragPayload<TSource, TContext> = {
      activeNode: activeEntry.node,
      overNode: overEntry.node,
      activeParentId: activeEntry.parentId,
      overParentId: overEntry.parentId,
      targetParentId: projectedDrop.parentId,
      activeIndex: activeEntry.index,
      overIndex: overEntry.index,
      targetIndex: projectedDrop.index,
      targetDepth: projectedDrop.depth,
      sameParent: activeEntry.parentId === overEntry.parentId,
      targetSameParent: activeEntry.parentId === projectedDrop.parentId,
      context,
    };
    void onDragEnd(payload);
    clearDragState();
  };

  const dragOverlay = (
    activeEntry && dragPreviewLayout ? (
      <div
        aria-hidden="true"
        className="pointer-events-none fixed z-[1000] min-w-0"
        style={{
          left: dragPreviewLayout.left,
          top: dragPreviewLayout.top,
          ...overlayStyle,
        }}
      >
        <div
          className={cn('min-w-0 overflow-hidden shadow-lg', classNames?.overlay)}
          style={overlayStyle}
        >
          <TreeItemRow
            node={activeEntry.node}
            depth={activeEntry.depth}
            indentSize={indentSize}
            selected={selectedNodeId === activeEntry.node.id}
            expanded={
              activeEntry.node.collapsible === false ||
              resolvedExpandedNodeIds.has(activeEntry.node.id)
            }
            active
            over={false}
            hasChildren={
              activeEntry.node.collapsible === true ||
              Boolean(activeEntry.node.children?.length)
            }
            disabled={Boolean(activeEntry.node.disabled)}
            selectable={activeEntry.node.selectable !== false}
            draggable={activeEntry.node.draggable !== false}
            activationMode={activationMode}
            dragHandleLabel={labels?.dragHandle ?? 'Drag'}
            expandLabel={labels?.expand ?? 'Expand'}
            collapseLabel={labels?.collapse ?? 'Collapse'}
            themeSlots={theme.slots}
            classNames={classNames}
            itemClassName={activeEntry.node.itemClassName}
            itemStyle={itemStyle}
            renderNodeActions={renderNodeActions}
            renderContext={{
              node: activeEntry.node,
              depth: activeEntry.depth,
              expanded:
                activeEntry.node.collapsible === false ||
                resolvedExpandedNodeIds.has(activeEntry.node.id),
              selected: selectedNodeId === activeEntry.node.id,
              dragging: true,
              disabled: Boolean(activeEntry.node.disabled),
              context: activeEntry.node.context ?? context,
            }}
          />
        </div>
      </div>
    ) : null
  );

  if (loading) {
    return (
      <div className={cn(theme.slots.treeRoot, classNames?.root, className)}>
        <div className={cn(theme.slots.treeLoading, classNames?.loading)}>
          {labels?.loading ?? 'Loading...'}
        </div>
      </div>
    );
  }

  if (nodes.length === 0) {
    return (
      <div className={cn(theme.slots.treeRoot, classNames?.root, className)}>
        <div className={cn(theme.slots.treeEmpty, classNames?.empty)}>
          {empty ?? labels?.empty ?? 'No data'}
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={(event) => {
        setOverNodeId(event.over ? String(event.over.id || '') : undefined);
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={clearDragState}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <div
          className={cn(
            theme.slots.treeRoot,
            'space-y-1 overflow-visible',
            classNames?.root,
            className,
          )}
          role="tree"
        >
          <div className={cn('min-w-0 space-y-1', classNames?.list)}>
            {flattenedNodes.map((entry) => {
              const node = entry.node;
              const expanded =
                node.collapsible === false || resolvedExpandedNodeIds.has(node.id);
              return (
                <DraggableTreeItem
                  key={node.id}
                  entry={entry}
                  selected={selectedNodeId === node.id}
                  expanded={expanded}
                  active={activeNodeId === node.id}
                  over={overNodeId === node.id && activeNodeId !== node.id}
                  indentSize={indentSize}
                  activationMode={activationMode}
                  dragHandleLabel={labels?.dragHandle ?? 'Drag'}
                  expandLabel={labels?.expand ?? 'Expand'}
                  collapseLabel={labels?.collapse ?? 'Collapse'}
                  themeSlots={theme.slots}
                  classNames={classNames}
                  itemStyle={itemStyle}
                  context={context}
                  renderNodeActions={renderNodeActions}
                  renderNodeChildren={renderNodeChildren}
                  onSelect={(nextNode) => onSelectNode?.(nextNode, context)}
                  onToggle={(nodeId) => setNodeExpanded(nodeId, !expanded)}
                  registerNodeElement={registerNodeElement}
                />
              );
            })}
          </div>
        </div>
      </SortableContext>
      {typeof document === 'undefined'
        ? dragOverlay
        : dragOverlay
          ? createPortal(dragOverlay, document.body)
          : null}
    </DndContext>
  );
}
